import type { Direction } from './popout-types';

const MIN_EDGE_DISTANCE = 4;

export interface PopoutLayoutBox {
  anchorWidth: number;
  anchorHeight: number;
  anchorLeft: number;
  anchorTop: number;
  contentWidth: number;
  contentHeight: number;
}

export interface PopoutLayoutOffsets {
  direction: Direction;
  alignment: 'left' | 'center' | 'right';
  offsetTop: number;
  offsetLeft: number;
}

export interface PopoutLayoutResult {
  nextX: number;
  nextY: number;
  effectiveDirection: Direction;
}

function computeHorizontalPlacement({
  alignment,
  anchorLeft,
  anchorWidth,
  contentWidth,
  offsetLeft,
  boundaryLeft
}: {
  alignment: PopoutLayoutOffsets['alignment'];
  anchorLeft: number;
  anchorWidth: number;
  contentWidth: number;
  offsetLeft: number;
  boundaryLeft: number;
}): number {
  const anchorLeftX = anchorLeft - boundaryLeft;

  const coords = {
    left: {
      anchorX: anchorLeftX,
      offset: offsetLeft
    },
    right: {
      anchorX: anchorLeftX + anchorWidth,
      offset: offsetLeft - contentWidth
    },
    center: {
      anchorX: anchorLeftX + anchorWidth / 2,
      offset: offsetLeft - contentWidth / 2
    }
  }[alignment];

  if (!coords) {
    throw new Error('Unknown alignment variant.');
  }

  return coords.anchorX + coords.offset;
}

function computeVerticalPlacement({
  direction,
  anchorTopY,
  anchorHeight,
  contentHeight,
  offsetTop,
  boundingHeight,
  scrollTopY
}: {
  direction: Direction;
  anchorTopY: number;
  anchorHeight: number;
  contentHeight: number;
  offsetTop: number;
  boundingHeight: number;
  scrollTopY?: number;
}): Pick<PopoutLayoutResult, 'nextY' | 'effectiveDirection'> {
  const yDownward = anchorTopY + anchorHeight + offsetTop;
  const yUpward = anchorTopY - contentHeight - offsetTop;

  let downwardToCompare = yDownward;
  let upwardToCompare = yUpward;

  if (scrollTopY) {
    downwardToCompare = scrollTopY + anchorHeight + offsetTop;
    upwardToCompare = scrollTopY - contentHeight - offsetTop;
  }

  switch (direction) {
    default:
      throw new Error('Unknown direction variant.');

    case 'downward':
      if (
        downwardToCompare + contentHeight >
          boundingHeight - MIN_EDGE_DISTANCE &&
        upwardToCompare > MIN_EDGE_DISTANCE
      ) {
        return { nextY: yUpward, effectiveDirection: 'upward' };
      }

      return { nextY: yDownward, effectiveDirection: 'downward' };

    case 'upward':
      if (upwardToCompare < MIN_EDGE_DISTANCE) {
        return { nextY: yDownward, effectiveDirection: 'downward' };
      }

      return { nextY: yUpward, effectiveDirection: 'upward' };
  }
}

export function computeViewportFixedLayout(
  box: PopoutLayoutBox,
  options: PopoutLayoutOffsets
): PopoutLayoutResult {
  const {
    anchorWidth,
    anchorHeight,
    anchorLeft,
    anchorTop,
    contentWidth,
    contentHeight
  } = box;

  const { alignment, direction, offsetTop, offsetLeft } = options;

  const boundingWidth = window.innerWidth;
  const boundingHeight = window.innerHeight;

  let nextX = computeHorizontalPlacement({
    alignment,
    anchorLeft,
    anchorWidth,
    contentWidth,
    offsetLeft,
    boundaryLeft: 0
  });

  if (nextX + contentWidth > boundingWidth - MIN_EDGE_DISTANCE) {
    nextX = boundingWidth - contentWidth - MIN_EDGE_DISTANCE;
  }

  if (nextX < MIN_EDGE_DISTANCE) {
    nextX = MIN_EDGE_DISTANCE;
  }

  const { nextY, effectiveDirection } = computeVerticalPlacement({
    direction,
    anchorTopY: anchorTop,
    anchorHeight,
    contentHeight,
    offsetTop,
    boundingHeight
  });

  return { nextX, nextY, effectiveDirection };
}

export function computeOffsetParentLayout({
  box,
  relativeBoundaryRect,
  secondRelativeBoundaryRectTop,
  options
}: {
  box: PopoutLayoutBox;
  relativeBoundaryRect: DOMRectReadOnly;
  secondRelativeBoundaryRectTop?: number;
  options: PopoutLayoutOffsets;
}): PopoutLayoutResult {
  const {
    anchorWidth,
    anchorHeight,
    anchorLeft,
    anchorTop,
    contentWidth,
    contentHeight
  } = box;

  const { alignment, direction, offsetTop, offsetLeft } = options;

  const relativeBoundaryAnchorTopY = anchorTop - relativeBoundaryRect.top;
  const secondBoundaryAnchorTopY =
    secondRelativeBoundaryRectTop !== undefined
      ? anchorTop - secondRelativeBoundaryRectTop
      : undefined;

  let nextX = computeHorizontalPlacement({
    alignment,
    anchorLeft,
    anchorWidth,
    contentWidth,
    offsetLeft,
    boundaryLeft: relativeBoundaryRect.left
  });

  const boundingWidth = relativeBoundaryRect.width;
  const boundingHeight = relativeBoundaryRect.height;

  if (nextX + contentWidth > boundingWidth - MIN_EDGE_DISTANCE) {
    nextX = boundingWidth - contentWidth - offsetLeft;
  }

  if (nextX < MIN_EDGE_DISTANCE) {
    nextX = offsetLeft;
  }

  const { nextY, effectiveDirection } = computeVerticalPlacement({
    direction,
    anchorTopY: relativeBoundaryAnchorTopY,
    scrollTopY: secondBoundaryAnchorTopY,
    anchorHeight,
    contentHeight,
    offsetTop,
    boundingHeight
  });

  return { nextX, nextY, effectiveDirection };
}
